import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  processExcelFile,
  uploadExcelFileInChunks,
  goToNextPage,
  goToPreviousPage,
  cancelExcelUpload,
  setParsedData,
} from "../../store/slices/Factory/excelSlice";
import ILeadData, { IAddress } from "../../models/LeadData.model";
import { getStatesData } from "../../store/slices/kanbanSlice";
import ToggleSwitch from "../../components/Users/ToggleSwitch";
import Button, { ButtonType } from "../../components/Assets/Button";
import { sampleJson } from "../../utils/leadSample";
import XLSX from "xlsx";

const ExcelUpload: React.FC = () => {
  const dispatch: RootDispatch = useDispatch();
  const {
    parsedData,
    currentPageData,
    currentPage,
    totalPages,
    fileUploaded,
    loading,
    uploading,
    uploadProgress,
    message,
    failedLeads,
  } = useSelector((state: RootState) => state.excel);

  const statesData = useSelector((state: RootState) => state.kanban.statesData);

  const [isDragging, setIsDragging] = React.useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isScrapperImport, setIsScrapperImport] =
    React.useState<boolean>(false);
  const [isImportJson, setIsImportJson] = React.useState<boolean>(false);
  const [jsonText, setJsonText] = React.useState<string>("");
  const [jsonError, setJsonError] = React.useState<string>("");
  const [address, setAddress] = useState<IAddress>({
    line1: "",
    state: "",
    city: "",
    district: "",
    pincode: "",
  });
  const [isJsonParsed, setIsJsonParsed] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const copyToClipboard = async (obj: any, idx: number) => {
    const text = JSON.stringify(obj, null, 2);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopiedIndex(idx);
      setTimeout(() => setCopiedIndex(null), 4000);
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  useEffect(() => {
    if (statesData.length === 0) {
      dispatch(getStatesData());
    }
  }, [dispatch, statesData.length]);

  const processFile = useCallback(
    (file: File, isScrapper: boolean) => {
      dispatch(processExcelFile({ file, isScrapper }));
    },
    [dispatch],
  );

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file, isScrapperImport);
    }
  };

  const handleCancel = async () => {
    await dispatch(cancelExcelUpload());
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleJsonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonText(e.target.value);
  };

  const handleImportJson = () => {
    setJsonError("");
    if (!jsonText.trim()) {
      setJsonError("Please paste valid JSON before importing.");
      return;
    }

    try {
      const parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed)) {
        setJsonError("JSON must be an array of lead objects.");
        return;
      }

      if (!address.state || !address.district) {
        setJsonError("Please select State and District before importing JSON.");
        return;
      }

      const mapped: ILeadData[] = parsed.map((item: any) => {
        const name = item.vendorName ?? item.vendor_name ?? item.name ?? "";
        const contactPersonName =
          item.contactPersonName ?? item.contact_person_name ?? "";
        const mobileNumber =
          item.contactPersonMobile ??
          item.contact_person_mobile ??
          item.mobileNumber ??
          "";
        const email =
          item.contactPersonEmail ??
          item.contact_person_email ??
          item.email ??
          "";
        const addressLine1 = item.address ?? item.addressLine1 ?? "";

        return {
          name,
          contactPersonName,
          mobileNumber,
          email,
          addressLine1,
          addressState: address.state.toLowerCase(),
          addressdistrict: address?.district?.toLowerCase(),
        } as unknown as ILeadData;
      });

      dispatch(setParsedData(mapped));
      setIsJsonParsed(true);
      setIsImportJson(false);
      setJsonText("");
    } catch (err: any) {
      setJsonError(err.message || "Invalid JSON");
    }
  };

  const handleDownloadExcel = () => {
    if (!parsedData.length) return;

    const worksheet = XLSX.utils.json_to_sheet(parsedData);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");

    XLSX.writeFile(workbook, "parsed_leads.xlsx");
  };

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);

      const file = e.dataTransfer.files?.[0];
      if (file) {
        processFile(file, isScrapperImport);
      }
    },
    [processFile, isScrapperImport],
  );

  const handleSave = async () => {
    const result = await dispatch(uploadExcelFileInChunks());
    // Clear file input after successful upload
    if (
      uploadExcelFileInChunks.fulfilled.match(result) &&
      fileInputRef.current
    ) {
      fileInputRef.current.value = "";
    }
  };

  const handleNextPage = () => {
    dispatch(goToNextPage());
  };

  const handlePreviousPage = () => {
    dispatch(goToPreviousPage());
  };

  const handlePasteSample = () => setJsonText(sampleJson);
  const handleClearJson = () => setJsonText("");

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-xl">
        {/* Header */}
        <div className="px-4 py-6 sm:px-6 sm:py-8 bg-gradient-to-r from-blue-600 to-purple-600">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center">
            Lead Import
          </h2>
          <p className="mt-2 text-sm md:text-sm text-blue-100 text-center">
            Upload your Excel or JSON files to add leads in the system.
          </p>
        </div>
        <div className="p-4 pb-0">
          <div className="flex items-center justify-center gap-3">
            <Button
              onClick={() => {
                setIsImportJson(false);
                setJsonText("");
                setJsonError("");
              }}
              className={`text-sm ${
                !isImportJson
                  ? "bg-emerald-600 text-white shadow-md"
                  : "bg-white !text-gray-700 border border-gray-200 hover:bg-gray-50 "
              }`}
              type={ButtonType.Extra}
            >
              Excel Format
            </Button>

            <Button
              onClick={() => {
                setIsImportJson(true);
                setJsonError("");
              }}
              className={`text-sm ${
                isImportJson
                  ? "bg-emerald-600 text-white"
                  : "bg-white !text-gray-700 border border-gray-200 hover:bg-gray-50"
              }`}
              type={ButtonType.Extra}
            >
              JSON Format
            </Button>
          </div>

          <div className="mt-2 text-xs text-gray-500 text-center">
            Choose import mode
          </div>
          {!isImportJson && (
            <div className="mt-6 flex justify-center">
              <ToggleSwitch
                id="scrapper-toggle"
                checked={isScrapperImport}
                onChange={(checked) => setIsScrapperImport(checked)}
                label="Scrapper Mode:"
                showIcon={false}
                containerClassName="inline-block flex items-center"
                sizeVariant="sm"
                showStatusText={true}
              />
            </div>
          )}
        </div>

        {/* File Upload Section */}
        <div className="p-4">
          {!isImportJson ? (
            <div
              className={`flex flex-col items-center justify-center border-2 border-dashed ${
                isDragging ? "border-blue-500" : "border-blue-300"
              } rounded-lg p-6 sm:p-8 bg-blue-50 hover:bg-blue-100 transition-colors duration-200`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx, .xls"
                onChange={handleFileUpload}
                disabled={loading || uploading}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center space-y-2"
              >
                <svg
                  className="w-10 h-10 sm:w-12 sm:h-12 text-blue-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  />
                </svg>
                <span className="text-blue-600 font-medium text-sm sm:text-base">
                  {loading
                    ? "Processing file..."
                    : uploading
                      ? "Uploading..."
                      : "Choose a file or drag it here"}
                </span>
                <span className="text-xs sm:text-sm text-gray-500">
                  Supported formats: .xlsx, .xls
                </span>
              </label>
            </div>
          ) : (
            <div className="bg-white border border-gray-100 rounded-lg shadow-md p-4">
              <div className="flex items-start gap-4">
                {/* left side: meta + selects */}
                <div className="w-full sm:w-1/3">
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mt-1">
                      Paste a JSON array of Lead objects.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-600">
                      State
                    </label>
                    <select
                      value={address.state}
                      onChange={(e) =>
                        setAddress((a) => ({
                          ...a,
                          state: e.target.value,
                          district: "",
                        }))
                      }
                      className="w-full p-2 text-xs border border-gray-200 rounded"
                    >
                      <option value="">Select State</option>
                      {statesData?.map((s) => (
                        <option key={s.state} value={s.state}>
                          {s.state.capitalizeWords()}
                        </option>
                      ))}
                    </select>

                    <label className="text-xs font-medium text-gray-600">
                      District
                    </label>
                    <select
                      value={address.district}
                      onChange={(e) =>
                        setAddress((a) => ({ ...a, district: e.target.value }))
                      }
                      disabled={!address.state}
                      className="w-full p-2 text-xs border border-gray-200 rounded disabled:opacity-60"
                    >
                      <option value="">Select District</option>
                      {statesData
                        .find((s) => s.state === address.state)
                        ?.districts?.map((d: string) => (
                          <option key={d} value={d}>
                            {d.capitalizeWords()}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="mt-9 flex flex-col gap-2">
                    <Button
                      onClick={handlePasteSample}
                      type={ButtonType.Cancel}
                      className="text-xs"
                    >
                      Paste Example
                    </Button>
                    <Button
                      onClick={handleClearJson}
                      type={ButtonType.Cancel}
                      className="text-xs !bg-gray-50"
                    >
                      Clear
                    </Button>
                  </div>
                </div>

                {/* right side: textarea */}
                <div className="w-full sm:w-2/3">
                  <textarea
                    value={jsonText}
                    onChange={handleJsonChange}
                    placeholder="Paste JSON array here..."
                    className="w-full min-h-[220px] p-3 border border-gray-200 rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 resize-none"
                  />

                  {jsonError && (
                    <p className="text-xs text-red-600 italic mt-2">
                      {jsonError}
                    </p>
                  )}

                  <div className="mt-3 flex items-center gap-2 justify-end">
                    <Button
                      onClick={handleImportJson}
                      className="w-full"
                      type={ButtonType.Extra}
                    >
                      Parse JSON
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Save Button */}
        {uploading ? (
          <div className="px-4 sm:px-6 pb-6 sm:pb-8">
            <div className="max-w-md mx-auto space-y-4">
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div
                  className="bg-blue-500 h-4 rounded-full transition-all duration-200 ease-in-out"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-center text-sm text-blue-600">
                Uploading... {uploadProgress}%
              </p>
              <div className="flex flex-col items-center pt-2 ">
                <Button onClick={handleCancel} type={ButtonType.Error}>
                  Cancel Upload
                </Button>
                <p className="text-xs text-gray-500 mt-2 italic">
                  Note: Uploaded leads will be saved before cancellation
                </p>
              </div>
            </div>
          </div>
        ) : (
          fileUploaded && (
            <div className="flex justify-center px-4 sm:px-6 pb-6 sm:pb-8">
              <button
                onClick={handleSave}
                disabled={uploading || loading}
                className="bg-navyBlue-primary text-white px-4 py-2 sm:px-6 sm:py-2 rounded-md hover:bg-navyBlue-light transition-colors duration-200 disabled:bg-blue-300 text-sm sm:text-base"
              >
                {loading ? "Processing..." : "Save Data"}
              </button>
            </div>
          )
        )}

        {/* Message Display */}
        {!uploading && message && (
          <div className="px-4 sm:px-6 py-4">
            <p
              className={`text-center text-sm font-medium ${
                message.includes("successfully")
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {message}
            </p>
          </div>
        )}

        {failedLeads?.length > 0 && (
          <div className="px-4 sm:px-6 py-6 sm:py-8">
            <h3 className="text-xl font-bold text-red-700 mb-4">
              Failed Records ({failedLeads.length})
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                      Row
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                      Reason
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                      Lead Data
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {failedLeads.map((f, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-2 text-xs">{f.row ?? "-"}</td>
                      <td className="px-4 py-2 text-xs">{f.reason}</td>
                      <td className="px-4 py-2 text-xs">
                        <div className="flex items-start gap-2">
                          <pre className="whitespace-pre-wrap text-xs flex-1">
                            {JSON.stringify(f.failedLead, null, 2)}
                          </pre>

                          <button
                            onClick={() => copyToClipboard(f.failedLead, idx)}
                            className="ml-2 inline-flex items-center gap-2 px-2 py-1 border rounded text-xs disabled:opacity-60"
                            title="Copy lead JSON"
                            aria-label="Copy lead JSON"
                            type="button"
                          >
                            {copiedIndex === idx ? (
                              <span className="font-semibold text-sm">
                                Copied!
                              </span>
                            ) : (
                              <>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                  aria-hidden="true"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2"
                                  />
                                  <rect
                                    x="10"
                                    y="10"
                                    width="10"
                                    height="10"
                                    rx="2"
                                    ry="2"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                  ></rect>
                                </svg>
                                <span className="sr-only">Copy</span>
                              </>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Parsed Data Display */}
        {parsedData.length > 0 && (
          <div className="px-4 sm:px-6 py-6 sm:py-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800">
                Parsed Data ({parsedData.length} records)
              </h3>

              {isJsonParsed && (
                <Button
                  onClick={handleDownloadExcel}
                  type={ButtonType.Extra}
                  className="text-xs inline-flex items-center gap-1"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Download Excel
                </Button>
              )}
            </div>
            <div className="overflow-x-scroll">
              <table className="max-w-full bg-white border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 sm:px-6 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Row Number
                    </th>
                    {parsedData.length > 0 &&
                      Object.keys(parsedData[0]).map((key) => (
                        <th
                          key={key}
                          className="px-4 py-2 sm:px-6 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {key}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentPageData.map((row, index) => (
                    <tr key={index}>
                      <td className="px-4 py-2 sm:px-6 sm:py-4 text-xs sm:text-sm text-gray-700">
                        {(currentPage - 1) * 10 + index + 1}
                      </td>
                      {Object.values(row).map((value, i) => (
                        <td
                          key={i}
                          className="px-4 py-2 sm:px-6 sm:py-4 text-xs sm:text-sm text-gray-700"
                        >
                          {String(value)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-4 mt-4">
                <Button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  type={ButtonType.Cancel}
                  className="text-xs"
                >
                  Previous
                </Button>

                <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>

                <Button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  type={ButtonType.Cancel}
                  className="text-xs"
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExcelUpload;
